import React from 'react';
import { Box, IconButton, Tooltip, Typography, Popover } from '@mui/material';
import { CheckCircle, Build, Edit } from '@mui/icons-material';
import { colors } from '../../../themes/typography';
import SelectionContext from '../../../contexts/SelectionContext';
import dayjs from 'dayjs';
import CreateExperimentForm from '../../shared/CreateExperimentForm';
import { API_BASE_URL, createAuthHeader } from '../../../config/api.config';
import axios from 'axios';

/**
 * AlertActions component - one-click "Is this broken?" (broken = under review until the product sells again)
 */
const AlertActions = ({ 
  alert,
  onStatusChange,
  loading = false,
  t
}) => {
  // Get selection context - will be null if not in a selection provider
  const selectionContext = React.useContext(SelectionContext);
  
  const {
    selectedIds = new Set(),
    selectedCount = 0, 
    isMultiSelect = false,
    onBulkAlertAction,
    onSingleAlertAction
  } = selectionContext || {};
  // Experiment creation popover (after "not broken")
  const [createAnchorEl, setCreateAnchorEl] = React.useState(null);
  const lastAlertIdRef = React.useRef(null);
  const experimentCreatedRef = React.useRef(false);


  const handleNotBroken = (event) => {
    // Check if we're in a selection context and have multiple selected
    if (selectionContext && isMultiSelect) {
      // Bulk action on all selected alerts
      const selectedIdArray = Array.from(selectedIds);
      if (onBulkAlertAction) {
        onBulkAlertAction(selectedIdArray, 'not_broken');
      }
    } else if (onSingleAlertAction || onStatusChange) {
      // Single action: straight to the experiment form
      setCreateAnchorEl(event.currentTarget);
    }
  };

  // Closing the form without an experiment still records "not broken"
  const handleCreateClose = async () => {
    setCreateAnchorEl(null);
    if (!experimentCreatedRef.current) {
      if (selectionContext && onSingleAlertAction) {
        await onSingleAlertAction(alert.id, 'not_broken');
      } else if (onStatusChange) {
        await onStatusChange('not_broken');
      }
    }
    experimentCreatedRef.current = false;
  };

  const handleBroken = async () => {
    try {
      // Check if we're in a selection context and have multiple selected
      if (selectionContext && isMultiSelect) {
        // Bulk action on all selected alerts
        const selectedIdArray = Array.from(selectedIds);
        if (onBulkAlertAction) {
          await onBulkAlertAction(selectedIdArray, 'mark_as_broken');
        }
      } else if (selectionContext && onSingleAlertAction) {
        await onSingleAlertAction(alert.id, 'mark_as_broken');
      } else if (onStatusChange) {
        await onStatusChange('mark_as_broken');
      }
    } catch (error) {
      console.error('Failed to mark as broken:', error);
    }
  };

  // Render based on alert status field
  const alertStatus = alert.status || 'pending';

  // Not broken alerts should never appear (filtered by backend)
  if (alertStatus === 'not_apply') {
    return null;
  }

  if (alertStatus === 'resolved') {
    // Resolved state - show completion status
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        py: '4px'
      }}>
        <Typography sx={{
          fontSize: '9px',
          color: '#34C759', // Apple green
          fontWeight: 600,
          textAlign: 'center',
          px: '8px',
          py: '2px',
          borderRadius: '6px', // Slightly more rounded for Apple feel
          backgroundColor: 'rgba(52, 199, 89, 0.08)', // Apple green background
          border: '1px solid rgba(52, 199, 89, 0.12)'
        }}>
          {t ? t('resolved') : 'Resolved'} • {dayjs(alert.resolution_date).format('MMM DD')}
        </Typography>
      </Box>
    );
  }

  // Under review: closes by itself when the product sells again; it can still be marked as working
  const isUnderReview = alertStatus === 'under_review';

  // Default: pending / under review - show question text and buttons inline
  return (
    <Box sx={{
      display: 'flex',
      gap: '8px',
      justifyContent: 'center',
      alignItems: 'center',
      py: '8px'
    }}>
      {/* Question text inline with buttons */}
      <Typography sx={{
        fontSize: '10px',
        color: colors.text.primary,
        fontWeight: 500,
        lineHeight: 1.2,
        whiteSpace: 'nowrap'
      }}>
        {isUnderReview
          ? (t ? t('underReview') : 'Under review')
          : (t ? t('isThisBroken') : 'Is this slot broken?')}
      </Typography>
        <Tooltip title={t ? t('markAsNotBroken') : 'Mark as working'} arrow>
        <IconButton
          onClick={handleNotBroken}
          disabled={loading}
          sx={{
            width: '24px',
            height: '24px',
            backgroundColor: 'transparent',
            border: '1px solid rgba(107, 114, 128, 0.2)', // Subtle gray border like experiment cards
            borderRadius: '4px', // Smaller radius
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.05)',
              borderColor: 'rgba(34, 197, 94, 0.3)',
            },
            '&:active': {
              transform: 'scale(0.98)'
            },
            '&:disabled': {
              opacity: 0.4
            }
          }}
        >
          <CheckCircle sx={{ 
            fontSize: '12px', 
            color: '#8E8E93' // Apple gray
          }} />
        </IconButton>
      </Tooltip>

      {!isUnderReview && (
      <Tooltip title={t ? t('markAsBroken') : 'Report issue'} arrow>
        <IconButton
          onClick={handleBroken}
          disabled={loading}
          sx={{
            width: '24px',
            height: '24px',
            backgroundColor: 'transparent',
            border: '1px solid rgba(107, 114, 128, 0.2)', // Subtle gray border like experiment cards
            borderRadius: '4px', // Smaller radius
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.05)',
              borderColor: 'rgba(249, 115, 22, 0.3)',
            },
            '&:active': {
              transform: 'scale(0.98)'
            },
            '&:disabled': {
              opacity: 0.4
            }
          }}
        >
          <Build sx={{ 
            fontSize: '12px', 
          color: '#8E8E93' // Apple gray
          }} />
        </IconButton>
      </Tooltip>
      )}

      {/* Create Experiment popover */}
      <Popover
        open={Boolean(createAnchorEl)}
        anchorEl={createAnchorEl}
        onClose={handleCreateClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: '12px',
            border: `1px solid ${colors.border.light}`,
            boxShadow: `0 8px 32px rgba(0,0,0,0.08)`,
            backdropFilter: 'blur(16px)',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            mt: '8px'
          }
        }}
      >
        <CreateExperimentForm
          open={Boolean(createAnchorEl)}
          column={{ id: 'pending' }}
          currentFilters={{}}
          filterOptions={{ groups: [] }}
          clientId={selectionContext?.clientId}
          allowPriceIncrease={false}
          onClose={handleCreateClose}
          onSuccess={async () => {
            experimentCreatedRef.current = true;
            // Refresh table after experiment creation
            if (selectionContext?.onDataRefresh) {
              await selectionContext.onDataRefresh();
            }
            setCreateAnchorEl(null);
          }}
          onError={() => {}}
          t={t}
          beforeSubmit={async () => {
            // First, mark alert as not broken and capture returned ID
            const token = localStorage.getItem('access_token');
            const resp = await axios.post(
              `${API_BASE_URL}/alerts/update?clientId=${selectionContext?.clientId}`,
              { alertIds: [alert.id], action: 'confirm_not_broken', metadata: {} },
              { headers: createAuthHeader(token) }
            );
            const succ = resp.data?.results?.succeeded || [];
            lastAlertIdRef.current = (succ[0]?.alertId) ?? alert.id;
          }}
          extraRequestBody={{ alertId: lastAlertIdRef.current ?? alert.id }}
          initialValues={{
            dStoreId: alert.d_store_id || alert.store_id || alert.machine_id,
            dProductId: alert.d_product_id,
            productName: alert.product_name,
            machineDisplayName: `${alert.group || ''}${alert.group ? ' - ' : ''}${alert.location || ''}${(alert.machine_id || alert.store_id) ? ` (${alert.machine_id || alert.store_id})` : ''}`.trim()
          }}
          lockMachine
          lockProduct
          presetSelection={{
            dStoreId: alert.d_store_id || alert.store_id || alert.machine_id,
            dProductId: alert.d_product_id,
            machineLabel: `${alert.group || ''}${alert.group ? ' - ' : ''}${alert.location || ''}${(alert.machine_id || alert.store_id) ? ` (${alert.machine_id || alert.store_id})` : ''}`.trim(),
            productLabel: alert.product_name
          }}
        />
      </Popover>
    </Box>
  );
};

export default AlertActions;
